import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, InputNumber, Modal, Table, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { humanFileSize } from './utils';
import NewServer from './NewServer';

const BYTES_PER_GIB = 1024 * 1024 * 1024;
const MAX_RESIZE_SIZE_GIB = 20 * 1024;

interface ServerRow {
	id: number;
	device_name: string;
	name: string;
	state: string;
	storage_size: number;
	type: string;
}

interface GetServersResponse {
	ok: boolean;
	servers?: ServerRow[];
	err?: string;
}

interface DeleteServerResponse {
	ok?: boolean;
	task?: string;
	err?: string;
	error?: string;
}

interface ResizeServerResponse {
	ok?: boolean;
	task?: string;
	err?: string;
	error?: string;
}

interface ServerActionStateResponse {
	ok?: boolean;
	done?: boolean | number;
	state?: string;
	msg?: string;
	err?: string;
	error?: string;
}

interface DeleteTaskState {
	serverId: number;
	taskUuid: string;
	message: string;
}

interface ResizeTaskState {
	serverId: number;
	taskUuid: string;
	message: string;
}

const sleep = (delayMs: number) => new Promise((resolve) => setTimeout(resolve, delayMs));

const normalizeError = (data: { err?: string; error?: string }): string => {
	if (data.err) {
		return data.err;
	}

	if (data.error) {
		return data.error;
	}

	return 'Unknown error';
};

const Severs: React.FC = () => {
	const [data, setData] = useState<ServerRow[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [deleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false);
	const [deleteDialogServer, setDeleteDialogServer] = useState<ServerRow | null>(null);
	const [deleteDialogText, setDeleteDialogText] = useState<string>('');
	const [deleteDialogError, setDeleteDialogError] = useState<string>('');
	const [deleteSubmitting, setDeleteSubmitting] = useState<boolean>(false);
	const [deleteTask, setDeleteTask] = useState<DeleteTaskState | null>(null);
	const [resizeDialogVisible, setResizeDialogVisible] = useState<boolean>(false);
	const [resizeDialogServer, setResizeDialogServer] = useState<ServerRow | null>(null);
	const [resizeDialogSizeGb, setResizeDialogSizeGb] = useState<number | null>(null);
	const [resizeDialogError, setResizeDialogError] = useState<string>('');
	const [resizeSubmitting, setResizeSubmitting] = useState<boolean>(false);
	const [resizeTask, setResizeTask] = useState<ResizeTaskState | null>(null);
	const mountedRef = useRef<boolean>(true);

	useEffect(() => {
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const setSafeState = (setter: () => void) => {
		if (mountedRef.current) {
			setter();
		}
	};

	const fetchServers = async () => {
		setSafeState(() => setLoading(true));

		let jdata: GetServersResponse;
		try {
			const resp = await fetch('/api/get_servers', {
				method: 'GET',
				credentials: 'include',
			});
			jdata = await resp.json();
		} catch (error) {
			jdata = { ok: false, err: 'Connection issue with service' };
		}

		if ('err' in jdata || !jdata.ok) {
			Modal.info({
				title: 'Error while fetching data',
				content: <p>{jdata.err ? jdata.err : 'Unknown error'}</p>,
				onOk: () => {},
			});
			setSafeState(() => setLoading(false));
			return;
		}

		setSafeState(() => {
			setData(jdata.servers ? jdata.servers : []);
			setLoading(false);
		});
	};

	const openDeleteDialog = (record: ServerRow) => {
		setDeleteDialogServer(record);
		setDeleteDialogText('');
		setDeleteDialogError('');
		setDeleteDialogVisible(true);
	};

	const closeDeleteDialog = () => {
		if (deleteSubmitting || deleteTask !== null) {
			return;
		}

		setDeleteDialogVisible(false);
		setDeleteDialogServer(null);
		setDeleteDialogText('');
		setDeleteDialogError('');
	};

	const openResizeDialog = (record: ServerRow) => {
		const currentSizeGiB = Math.floor(record.storage_size / BYTES_PER_GIB);
		setResizeDialogServer(record);
		setResizeDialogSizeGb(Math.min(MAX_RESIZE_SIZE_GIB - 1, currentSizeGiB + 1));
		setResizeDialogError('');
		setResizeDialogVisible(true);
	};

	const closeResizeDialog = () => {
		if (resizeSubmitting || resizeTask !== null) {
			return;
		}

		setResizeDialogVisible(false);
		setResizeDialogServer(null);
		setResizeDialogSizeGb(null);
		setResizeDialogError('');
	};

	const pollDeleteState = async (taskUuid: string, serverId: number) => {
		while (mountedRef.current) {
			let stateData: ServerActionStateResponse;
			try {
				const formData = new FormData();
				formData.append('setup_uuid', taskUuid);

				const resp = await fetch('/api/server_action_state', {
					method: 'POST',
					credentials: 'include',
					body: formData,
				});
				stateData = await resp.json();
			} catch (error) {
				setSafeState(() => {
					setDeleteTask(null);
					Modal.info({
						title: 'Error while checking deletion progress',
						content: <p>Connection issue while checking deletion state</p>,
						onOk: () => {},
					});
				});
				await fetchServers();
				return;
			}

			if (!stateData.ok) {
				setSafeState(() => {
					setDeleteTask(null);
					Modal.info({
						title: 'Error while checking deletion progress',
						content: <p>{normalizeError(stateData)}</p>,
						onOk: () => {},
					});
				});
				await fetchServers();
				return;
			}

			if (stateData.msg) {
				setSafeState(() => {
					setDeleteTask({
						serverId,
						taskUuid,
						message: stateData.msg ? stateData.msg : 'Deleting server...',
					});
				});
			}

			if (stateData.done) {
				setSafeState(() => setDeleteTask(null));
				await fetchServers();
				message.success('Server deletion completed');
				return;
			}

			if (stateData.state === 'ERROR') {
				setSafeState(() => {
					setDeleteTask(null);
					Modal.info({
						title: 'Server deletion failed',
						content: <p>{stateData.msg ? stateData.msg : 'Server deletion failed'}</p>,
						onOk: () => {},
					});
				});
				await fetchServers();
				return;
			}

			await sleep(1000);
		}
	};

	const pollResizeState = async (taskUuid: string, serverId: number) => {
		while (mountedRef.current) {
			let stateData: ServerActionStateResponse;
			try {
				const formData = new FormData();
				formData.append('setup_uuid', taskUuid);

				const resp = await fetch('/api/server_action_state', {
					method: 'POST',
					credentials: 'include',
					body: formData,
				});
				stateData = await resp.json();
			} catch (error) {
				setSafeState(() => {
					setResizeTask(null);
					Modal.info({
						title: 'Error while checking resize progress',
						content: <p>Connection issue while checking resize state</p>,
						onOk: () => {},
					});
				});
				await fetchServers();
				return;
			}

			if (!stateData.ok) {
				setSafeState(() => {
					setResizeTask(null);
					Modal.info({
						title: 'Error while checking resize progress',
						content: <p>{normalizeError(stateData)}</p>,
						onOk: () => {},
					});
				});
				await fetchServers();
				return;
			}

			if (stateData.msg) {
				setSafeState(() => {
					setResizeTask({
						serverId,
						taskUuid,
						message: stateData.msg ? stateData.msg : 'Resizing server...',
					});
				});
			}

			if (stateData.state === 'RUNNING') {
				setSafeState(() => setResizeTask(null));
				await fetchServers();
				message.success('Server resize completed');
				return;
			}

			if (stateData.state === 'ERROR') {
				setSafeState(() => {
					setResizeTask(null);
					Modal.info({
						title: 'Server resize failed',
						content: <p>{stateData.msg ? stateData.msg : 'Server resize failed'}</p>,
						onOk: () => {},
					});
				});
				await fetchServers();
				return;
			}

			await sleep(1000);
		}
	};

	const handleDeleteServer = async () => {
		if (!deleteDialogServer) {
			return;
		}

		if (deleteDialogText.trim().toLowerCase() !== 'delete') {
			setDeleteDialogError("Type 'delete' to confirm deletion");
			return;
		}

		setDeleteSubmitting(true);
		setDeleteDialogError('');

		let jdata: DeleteServerResponse;
		try {
			const formData = new FormData();
			formData.append('appname', deleteDialogServer.device_name);

			const resp = await fetch('/api/delete_server', {
				method: 'POST',
				credentials: 'include',
				body: formData,
			});
			jdata = await resp.json();
		} catch (error) {
			jdata = { ok: false, err: 'Connection issue with service' };
		}

		if ('err' in jdata || !jdata.ok || !jdata.task) {
			setDeleteSubmitting(false);
			setDeleteDialogError(normalizeError(jdata));
			return;
		}

		setDeleteDialogVisible(false);
		setDeleteDialogServer(null);
		setDeleteDialogText('');
		setDeleteDialogError('');
		setDeleteSubmitting(false);
		setDeleteTask({
			serverId: deleteDialogServer.id,
			taskUuid: jdata.task,
			message: 'Starting deletion...',
		});
		message.info(`Deletion started for ${deleteDialogServer.name}`);
		await pollDeleteState(jdata.task, deleteDialogServer.id);
	};

	const handleResizeServer = async () => {
		if (!resizeDialogServer || resizeDialogSizeGb === null) {
			return;
		}

		const currentSizeGiB = resizeDialogServer.storage_size / BYTES_PER_GIB;
		const newSizeGiB = Math.floor(resizeDialogSizeGb);

		if (newSizeGiB <= currentSizeGiB) {
			setResizeDialogError('New size must be larger than the current size');
			return;
		}

		if (newSizeGiB >= MAX_RESIZE_SIZE_GIB) {
			setResizeDialogError('New size must be smaller than 20 TiB');
			return;
		}

		setResizeSubmitting(true);
		setResizeDialogError('');

		let jdata: ResizeServerResponse;
		try {
			const formData = new FormData();
			formData.append('appname', resizeDialogServer.device_name);
			formData.append('new_size', String(newSizeGiB * BYTES_PER_GIB));

			const resp = await fetch('/api/resize_server', {
				method: 'POST',
				credentials: 'include',
				body: formData,
			});
			jdata = await resp.json();
		} catch (error) {
			jdata = { ok: false, err: 'Connection issue with service' };
		}

		if ('err' in jdata || !jdata.ok || !jdata.task) {
			setResizeSubmitting(false);
			setResizeDialogError(normalizeError(jdata));
			return;
		}

		setResizeDialogVisible(false);
		setResizeDialogServer(null);
		setResizeDialogSizeGb(null);
		setResizeDialogError('');
		setResizeSubmitting(false);
		setResizeTask({
			serverId: resizeDialogServer.id,
			taskUuid: jdata.task,
			message: 'Starting resize...',
		});
		message.info(`Resize started for ${resizeDialogServer.name}`);
		await pollResizeState(jdata.task, resizeDialogServer.id);
	};

	useEffect(() => {
		fetchServers();
	}, []);

	const columns: ColumnsType<ServerRow> = [
		{
			title: 'Id',
			dataIndex: 'id',
			sorter: (a: ServerRow, b: ServerRow) => a.id - b.id,
		},
		{
			title: 'Appliance name',
			dataIndex: 'device_name',
			sorter: (a: ServerRow, b: ServerRow) => a.device_name.localeCompare(b.device_name),
		},
		{
			title: 'Type',
			dataIndex: 'type',
			sorter: (a: ServerRow, b: ServerRow) => a.type.localeCompare(b.type),
		},
		{
			title: 'State',
			dataIndex: 'state',
			render: (state: string, record: ServerRow) => {
				if (deleteTask && deleteTask.serverId === record.id) {
					return deleteTask.message;
				}

				return state;
			},
			sorter: (a: ServerRow, b: ServerRow) => a.state.localeCompare(b.state),
		},
		{
			title: 'Storage size',
			dataIndex: 'storage_size',
			render: (storage_size: number) => humanFileSize(storage_size, false),
			sorter: (a: ServerRow, b: ServerRow) => a.storage_size - b.storage_size,
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (_: unknown, record: ServerRow) => {
				const deleting = deleteTask && deleteTask.serverId === record.id;
				const resizing = resizeTask && resizeTask.serverId === record.id;
				const currentSizeGiB = Math.floor(record.storage_size / BYTES_PER_GIB);
				const canResize = record.state === 'RUNNING' && currentSizeGiB < MAX_RESIZE_SIZE_GIB - 1;

				if (deleting) {
					return <span>Deleting...</span>;
				}

				if (resizing) {
					return <span>Resizing...</span>;
				}

				return (
					<div style={{ display: 'flex', gap: 8 }}>
						<Button size="small" onClick={() => openResizeDialog(record)} disabled={!canResize}>
							Resize
						</Button>
						<Button danger size="small" onClick={() => openDeleteDialog(record)}>
							Delete
						</Button>
					</div>
				);
			},
		},
	];

	return (
		<div style={{ height: '100%' }}>
			<Table
				columns={columns}
				rowKey={(record: ServerRow) => record.id.toString()}
				dataSource={data}
				loading={loading}
				pagination={{ position: 'both', showSizeChanger: true, defaultPageSize: 50 }}
				title={() => (
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<span>Servers</span>
						<NewServer onCreated={fetchServers} />
					</div>
				)}
			/>
			<Modal
				title={deleteTask ? 'Deleting server' : 'Confirm server deletion'}
				visible={deleteDialogVisible || deleteTask !== null}
				onCancel={closeDeleteDialog}
				onOk={handleDeleteServer}
				okText={deleteTask ? 'Deleting' : 'Delete'}
				okButtonProps={{ disabled: deleteTask !== null || deleteDialogText.trim().toLowerCase() !== 'delete' }}
				cancelButtonProps={{ disabled: deleteTask !== null || deleteSubmitting }}
				confirmLoading={deleteSubmitting}
				destroyOnClose
			>
				{deleteTask ? (
					<div>
						<p>{deleteTask.message}</p>
					</div>
				) : (
					<div>
						<p>
							Type delete to confirm deletion of {deleteDialogServer ? deleteDialogServer.name : 'this server'}.
						</p>
						<Input
							value={deleteDialogText}
							onChange={(event) => setDeleteDialogText(event.target.value)}
							placeholder="delete"
							disabled={deleteSubmitting}
						/>
						{deleteDialogError ? <p style={{ color: '#cf1322', marginTop: 12 }}>{deleteDialogError}</p> : null}
					</div>
				)}
			</Modal>
			<Modal
				title={resizeTask ? 'Resizing server' : 'Resize server storage'}
				visible={resizeDialogVisible || resizeTask !== null}
				onCancel={closeResizeDialog}
				onOk={handleResizeServer}
				okText={resizeTask ? 'Resizing' : 'Resize'}
				okButtonProps={{ disabled: resizeTask !== null || resizeSubmitting || resizeDialogSizeGb === null }}
				cancelButtonProps={{ disabled: resizeTask !== null || resizeSubmitting }}
				confirmLoading={resizeSubmitting}
				destroyOnClose
			>
				{resizeTask ? (
					<div>
						<p>{resizeTask.message}</p>
					</div>
				) : (
					<div>
						<p>
							Choose a new size in GiB for {resizeDialogServer ? resizeDialogServer.name : 'this server'}.
						</p>
						<p>
							The new size must be larger than the current size and smaller than 20 TiB.
						</p>
						<InputNumber
							style={{ width: '100%' }}
							min={resizeDialogServer ? Math.floor(resizeDialogServer.storage_size / BYTES_PER_GIB) + 1 : 1}
							max={MAX_RESIZE_SIZE_GIB - 1}
							step={1}
							value={resizeDialogSizeGb === null ? undefined : resizeDialogSizeGb}
							onChange={(value) => setResizeDialogSizeGb(typeof value === 'number' ? value : null)}
							disabled={resizeSubmitting}
						/>
						{resizeDialogError ? <p style={{ color: '#cf1322', marginTop: 12 }}>{resizeDialogError}</p> : null}
					</div>
				)}
			</Modal>
		</div>
	);
};

export default Severs;
